import React from 'react';
import SectionHeader from './SectionHeader';
import CurrencyInput from './CurrencyInput';
import NotesInput from './NotesInput';

/**
 * Section 2 — Housing
 *
 * The main grid covers primary housing costs. Additional mortgages (second,
 * third, etc.) are captured dynamically at the bottom — each entry has its
 * own Principal & Interest, Escrow, and Property Taxes fields. Filling the
 * last entry automatically reveals a new empty one.
 *
 * @param {object}   data     - Current section data.
 * @param {function} onChange - Called with updated partial object.
 */
export default function StepHousing({ data = {}, onChange, onGoToIncome }) {
  function set(field, value) {
    onChange({ ...data, [field]: value });
  }

  const escrowFilled = !!data.escrow;

  // ── Primary mortgage fields (grouped together) ─────────────────────────
  const primaryMortgageFields = [
    { key: 'mortgage_or_rent', label: 'Mortgage (Principal & Interest) or Rent' },
    {
      key: 'escrow',
      label: 'Escrow',
      hint: 'Escrow covers property taxes + homeowners insurance. If entered, skip those two lines below.',
    },
    {
      key: 'property_taxes',
      label: 'Property Taxes',
      disabled: escrowFilled,
      hint: escrowFilled ? 'Already included in your Escrow amount above.' : undefined,
    },
    {
      key: 'homeowners_insurance',
      label: 'Homeowners / Renters Insurance',
      disabled: escrowFilled,
      hint: escrowFilled ? 'Already included in your Escrow amount above.' : undefined,
    },
    { key: 'pmi', label: 'Private Mortgage Insurance (PMI)', hint: 'If applicable — loan with < 20% down payment.' },
  ];

  // ── Other housing expense fields ────────────────────────────────────────
  const otherHousingFields = [
    { key: 'association_dues', label: 'Association Dues (HOA, Condo, etc.)' },
    { key: 'home_maintenance', label: 'Ongoing Home Maintenance' },
    { key: 'household_cleaning', label: 'Household Cleaning & Other Services' },
    { key: 'landscaping', label: 'Landscaping & Lawn Care Services' },
    { key: 'home_security', label: 'Home Security System' },
  ];

  // ── Additional mortgages ───────────────────────────────────────────────
  // Stored as an array of { principal, escrow, property_taxes, homeowners_insurance }.
  // A trailing empty slot is always shown; filling it reveals the next one (max 20).
  const MAX_MORTGAGES = 20;
  function hasValue(m) {
    return !!(m.principal || m.escrow || m.property_taxes || m.homeowners_insurance || m.nickname?.trim());
  }

  const stored = data.additional_mortgages?.length
    ? data.additional_mortgages
    : [{}];

  const displayMortgages =
    hasValue(stored[stored.length - 1]) && stored.length < MAX_MORTGAGES
      ? [...stored, {}]
      : stored;

  function setMortgage(index, field, value) {
    const updated = displayMortgages.map((m, i) =>
      i === index ? { ...m, [field]: value } : m
    );
    // Strip trailing empty entries, always keep at least one.
    let cleaned = updated;
    while (cleaned.length > 1 && !hasValue(cleaned[cleaned.length - 1])) {
      cleaned = cleaned.slice(0, -1);
    }
    onChange({ ...data, additional_mortgages: cleaned });
  }

  function mortgageLabel(i) {
    return `Mortgage ${i + 2}`;
  }

  // ── Totals ─────────────────────────────────────────────────────────────
  const allMainFields = [...primaryMortgageFields, ...otherHousingFields];
  const mainTotal = allMainFields.reduce((sum, f) => {
    if (f.disabled) return sum;
    return sum + (Number(data[f.key]) || 0);
  }, 0);

  const additionalTotal = stored.reduce((sum, m) => {
    return (
      sum +
      (Number(m.principal) || 0) +
      (Number(m.escrow) || 0) +
      (Number(m.property_taxes) || 0) +
      (Number(m.homeowners_insurance) || 0)
    );
  }, 0);

  const total = mainTotal + additionalTotal;

  return (
    <div>
      <SectionHeader
        number={2}
        title="Housing"
        tip="Your mortgage statement may show Principal, Interest, and Escrow
          separately. Add Principal + Interest together for the mortgage line.
          If you enter an Escrow amount, skip Homeowners Insurance and Property Taxes."
      />

      <div className="cashflow-field-grid">
        {primaryMortgageFields.map(({ key, label, hint, disabled }) => (
          <CurrencyInput
            key={key}
            id={key}
            label={label}
            value={data[key] ?? null}
            onChange={(val) => set(key, val)}
            hint={hint}
            disabled={!!disabled}
          />
        ))}
      </div>

      <div className="cashflow-field-grid mt-3">
        <p className="small text-muted cashflow-field-full mb-2">
          Enter annual amounts for other housing expenses below.
        </p>
        {otherHousingFields.map(({ key, label, hint, disabled }) => (
          <CurrencyInput
            key={key}
            id={key}
            label={label}
            value={data[key] ?? null}
            onChange={(val) => set(key, val)}
            hint={hint}
            disabled={!!disabled}
          />
        ))}
      </div>

      {/* ── Additional Mortgages ── */}
      <div className="mt-4">
        <p className="cashflow-subsection-title">Additional Mortgages</p>
        <p className="small text-muted mb-3">
          If this is a rental or investment property, remember to add the
          rental income as an income source on the{' '}
          <button
            type="button"
            className="btn btn-link btn-sm p-0 align-baseline"
            onClick={onGoToIncome}
          >
            Income step
          </button>
          .
        </p>
        {displayMortgages.map((m, i) => {
          const mEscrowFilled = !!m.escrow;
          const defaultLabel = mortgageLabel(i);
          return (
            <div key={i} className="cashflow-additional-mortgage mb-3">
              <div className="d-flex align-items-center gap-2 mb-2">
                <span className="small fw-semibold text-secondary">
                  {defaultLabel}
                </span>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder={`Nickname (e.g. "Rental Property")`}
                  value={m.nickname ?? ''}
                  onChange={(e) => setMortgage(i, 'nickname', e.target.value)}
                  style={{ flex: 1 }}
                />
              </div>
              <div className="cashflow-field-grid">
                <CurrencyInput
                  id={`am_${i}_principal`}
                  label="Principal & Interest"
                  value={m.principal ?? null}
                  onChange={(val) => setMortgage(i, 'principal', val)}
                />
                <CurrencyInput
                  id={`am_${i}_escrow`}
                  label="Escrow"
                  hint="Escrow covers property taxes + homeowners insurance. If entered, skip those two lines below."
                  value={m.escrow ?? null}
                  onChange={(val) => setMortgage(i, 'escrow', val)}
                />
                <CurrencyInput
                  id={`am_${i}_property_taxes`}
                  label="Property Taxes"
                  value={m.property_taxes ?? null}
                  onChange={(val) => setMortgage(i, 'property_taxes', val)}
                  disabled={mEscrowFilled}
                  hint={mEscrowFilled ? 'Already included in Escrow above.' : undefined}
                />
                <CurrencyInput
                  id={`am_${i}_homeowners_insurance`}
                  label="Homeowners / Renters Insurance"
                  value={m.homeowners_insurance ?? null}
                  onChange={(val) => setMortgage(i, 'homeowners_insurance', val)}
                  disabled={mEscrowFilled}
                  hint={mEscrowFilled ? 'Already included in Escrow above.' : undefined}
                />
              </div>
            </div>
          );
        })}
      </div>

      {total > 0 && (
        <div className="cashflow-total-box mt-3">
          <span className="fw-semibold">2: Total Housing Expenses</span>
          <span className="cashflow-total-amount">
            ${total.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
          </span>
        </div>
      )}

      <NotesInput value={data.notes} onChange={(val) => set('notes', val)} />
    </div>
  );
}
